import React, { useState, useEffect, useRef } from 'react';
import { 
  Package, 
  User, 
  Mail, 
  DollarSign, 
  CreditCard, 
  Clock, 
  X,
  Bell,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mapPaymentMethodToDisplay, mapPaymentStatusToDisplay, mapOrderStatusToDisplay, getStatusColor } from '../../utils/paymentUtils';

/**
 * OrderNotificationPopup - Primary notification component for new orders
 * This component displays a modal popup with order details when a new order is received.
 * It includes sound notification and provides quick actions to view order details.
 * 
 * Note: This is the main notification method. Toast notifications have been disabled
 * to prevent duplicate notifications for the same order.
 */
const OrderNotificationPopup = ({ order, onClose, onViewOrder }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const audioContextRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('🎬 OrderNotificationPopup mounted, order:', order);
    // Show notification with animation
    const timer = setTimeout(() => {
      console.log('🎬 Showing notification popup');
      setIsVisible(true);
      // Play notification sound
      playNotificationSound();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const playNotificationSound = async () => {
    try {
      console.log('🔊 Playing notification sound...');
      
      // Create audio context if it doesn't exist
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        console.log('🎵 Audio context created');
      }

      const audioContext = audioContextRef.current;
      
      // Resume audio context if suspended (required for autoplay policies)
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
        console.log('🎵 Audio context resumed');
      }
      
      // Create oscillator for notification sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Configure sound
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800Hz
      oscillator.type = 'sine';

      // Configure volume envelope
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      // Play sound
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
      
      console.log('🔊 Notification sound played successfully');
    } catch (error) {
      console.error('❌ Notification sound failed:', error);
    }
  };

  const handleViewOrder = () => {
    if (onViewOrder) {
      onViewOrder(order);
    } else {
      navigate(`/admin/orders`);
    }
    handleClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getPaymentMethodIcon = (method) => {
    if (!method) return <CreditCard className="w-4 h-4" />;
    
    const methodLower = method.toLowerCase();
    if (methodLower.includes('cash') || methodLower.includes('cod')) {
      return <DollarSign className="w-4 h-4" />;
    }
    if (methodLower.includes('offline')) {
      return <Clock className="w-4 h-4" />;
    }
    return <CreditCard className="w-4 h-4" />;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'text-green-600';
      case 'processing':
        return 'text-blue-600';
      case 'pending':
        return 'text-yellow-600';
      case 'cancelled':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Notification Popup */}
      <div 
        className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 transition-all duration-300 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        } ${isExiting ? 'scale-95 opacity-0' : ''}`}
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 sm:p-6 text-white relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Bell className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">New Order Received!</h3>
                  <p className="text-blue-100 text-sm">{formatTime(order.created_at)}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-white hover:text-blue-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4">
            {/* Order Number */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Order Number</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">{order.order_number}</span>
            </div>

            {/* Customer Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-900 dark:text-white font-medium">
                  {order.customer_name}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {order.customer_email}
                </span>
              </div>
            </div>

            {/* Order Details */}
            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatCurrency(order.total_amount)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {getPaymentMethodIcon(order.payment_method)}
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {mapPaymentMethodToDisplay(order.payment_method)}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Package className="w-4 h-4 text-blue-600" />
                  <span className={`text-xs font-medium ${getStatusColor(order.order_status, 'order')}`}>
                    {mapOrderStatusToDisplay(order.order_status)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <CreditCard className="w-4 h-4 text-purple-600" />
                  <span className={`text-xs font-medium ${getStatusColor(order.payment_status, 'payment')}`}>
                    {mapPaymentStatusToDisplay(order.payment_status)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 space-y-3">
            <button
              onClick={handleViewOrder}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2"
            >
              <ExternalLink className="w-4 h-4" />
              <span>View Order Details</span>
            </button>
            <button
              onClick={handleClose}
              className="w-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderNotificationPopup;
